import { defineStore } from "pinia";
import { useUserStore } from "./user";
import { cleanUpMediaUrls, fetchImageAsBlobUrl } from "@/utils/files";

import api from "@/api";

export const FALLBACK_BRANDING = {
  botName: "Ezee Assist Bot",
  botLogoUrl: "/static/ezee.png",
  orgLogoUrl: "/static/ezee.png",
}

export const useOrganizationStore = defineStore("organization", {
  state: () => ({
    allDict: {},
    myOrganization: {},
    myOrganizationAccessChannelsDetails: {},
    myOrganizationMediaUrls: {
      bot_icon_url: "",
      org_logo_url: "",
    },
    previousBlobMediaUrls: [],
    myOrganizationIsProco: false,
    myOrgEnablements: [],
    loading: false,
    dropdownOptions: [],
  }),
  getters: {
    botName() {
      return this.myOrganization?.bot_name || FALLBACK_BRANDING.botName;
    },
    botLogoUrl() {
      return this.myOrganization?.bot_logo_url || "";
    },
    organizationId() {
      return this.myOrganization?.id || "";
    },
  },
  actions: {
    async fetchAll() {
      this.loading = true;
      try {
        const res = await api.organization.fetch(null, null, false);
        res.data = res.data.sort((a, b) => a.name.localeCompare(b.name));
        this.allDict = res.data.reduce((dict, org) => {
          dict[org.id] = org;
          return dict;
        }, {});

        this.dropdownOptions = Object.values(this.allDict).map(
          (organization) => {
            return {
              text: organization.name,
              value: organization.id,
            };
          }
        );
      } catch (error) {
        console.error(error.message);
      } finally {
        this.loading = false;
      }
    },
    async fetchMyOrganization() {
      this.loading = true;
      try {
        const userStore = useUserStore();
        const activeOrganization = userStore.activeOrganization ?? null;
        const res =
          await api.organization.fetchMyOrganization(activeOrganization);
        this.myOrganization = res.data.organization;
        this.myOrgEnablements = res.data.enablements;
        this.myOrganizationMediaUrls = res.data.media_urls;
        this.myOrganizationAccessChannelsDetails =
          res.data.access_channels_details;
        this.myOrganizationIsProco = res.data.is_proco_org;

        if (this.myOrganization?.name) {
          document.title = `${this.myOrganization.name} - EZee Assist`;
        } else {
          document.title = "EZee Assist";
        }
      } catch (error) {
        console.error(error.message);
      } finally {
        this.loading = false;
      }
    },
    // updates the organization to have the current state values
    async updateOrganizationProperties(updates) {
      if (!this.myOrganization) {
        return;
      }

      try {
        // Call the updated API function
        const res = await api.organization.updateOrganizationPropertiesApi(
          this.myOrganization?.id,
          updates
        );
        this.myOrganization = res.data;

        // clean up old blobs in memory
        if (this.previousBlobMediaUrls.length) {
          this.previousBlobMediaUrls = cleanUpMediaUrls(
            this.previousBlobMediaUrls
          );
        }

        // download the images into memory
        const updatedMediaUrlsEntries = await Promise.all(
          Object.entries(res.media_urls).map(async ([key, value]) => {
            const url = await fetchImageAsBlobUrl(value);
            this.previousBlobMediaUrls.push(url);
            return [key, url];
          })
        );

        const updatedMediaUrls = Object.fromEntries(updatedMediaUrlsEntries);
        // update the store with the new memory links
        this.myOrganizationMediaUrls = updatedMediaUrls;
      } catch (error) {
        console.error(`Failed to update organization`, error);
      }
    },

    updateOrganizationStoreProperties(updates) {
      if (typeof updates === "object" && !Array.isArray(updates)) {
        for (const [key, value] of Object.entries(updates)) {
          if (key in this.myOrganization) {
            this.myOrganization[key] = value;
          } else {
            console.warn(`Property ${key} does not exist on myOrganization.`);
          }
        }
      } else {
        console.warn(
          "updateOrganizationProperty expects an object of key-value pairs."
        );
      }
    },
    // fallback urls if bot image url breaks
    useFallbackBotLogoUrl(event) {
      if (!event.target.src.includes(FALLBACK_BRANDING.botLogoUrl)) {
        this.myOrganizationMediaUrls.bot_icon_url = FALLBACK_BRANDING.botLogoUrl;
      }
    },
    // fallback url if org image url breaks
    useFallbackOrgLogoUrl(event) {
      if (!event.target.src.includes(FALLBACK_BRANDING.orgLogoUrl)) {
        this.myOrganizationMediaUrls.org_logo_url = FALLBACK_BRANDING.orgLogoUrl;
      }
    },
  },
});
